import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';

// Pages
import LoginPage from './pages/LoginPage';
import BackOfficeLayout from './pages/BackOfficeLayout';
import ClientPortalLayout from './pages/ClientPortalLayout';
import BackOfficeDashboard from './pages/backoffice/Dashboard';
import TicketListPage from './pages/backoffice/TicketList';
import UserManagement from './pages/backoffice/UserManagement';
import AnalyticsPage from './pages/backoffice/Analytics';
import TagManagement from './pages/backoffice/TagManagement';
import NotificationCenter from './pages/backoffice/NotificationCenter';
import ClientDashboard from './pages/client/Dashboard';
import NewTicketPage from './pages/client/NewTicket';
import ClientSettings from './pages/client/Settings';
import TicketDetailPage from './pages/TicketDetail';

import './index.css';

// Protected Route wrapper
const ProtectedRoute = ({ children, staffOnly = false }) => {
  const { isAuthenticated, loading, isStaff } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  if (staffOnly && !isStaff) {
    return <Navigate to="/portal" replace />;
  }
  
  return children;
};

// Client Route wrapper
const ClientRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { isAuthenticated, isStaff, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Route */}
      <Route 
        path="/" 
        element={
          isAuthenticated 
            ? <Navigate to={isStaff ? "/backoffice" : "/portal"} replace /> 
            : <LoginPage />
        } 
      />
      
      {/* Back Office Routes (Staff) */}
      <Route
        path="/backoffice"
        element={
          <ProtectedRoute staffOnly>
            <NotificationProvider>
              <BackOfficeLayout />
            </NotificationProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<BackOfficeDashboard />} />
        <Route path="tickets" element={<TicketListPage />} />
        <Route path="tickets/:ticketId" element={<TicketDetailPage />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="tags" element={<TagManagement />} />
        <Route path="notifications" element={<NotificationCenter />} />
      </Route>
      
      {/* Client Portal Routes */}
      <Route
        path="/portal"
        element={
          <ClientRoute>
            <NotificationProvider>
              <ClientPortalLayout />
            </NotificationProvider>
          </ClientRoute>
        }
      >
        <Route index element={<ClientDashboard />} />
        <Route path="new" element={<NewTicketPage />} />
        <Route path="tickets/:ticketId" element={<TicketDetailPage />} />
        <Route path="settings" element={<ClientSettings />} />
        <Route path="notifications" element={<NotificationCenter />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              className: 'font-sans'
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
